/* jshint browser:true */

/**
 * Utilities to assist with the user interface.
 */
export default class UiUtils {
  /**
   * Returns true if an element is within the currently scrolled area.
   * @param {HTMLElement | string} selector An element or a query selector for
   *  an HTML element.
   */
  static isScrolledIntoView(selector) {
    const el =
      typeof selector === 'string'
        ? document.querySelector(selector)
        : selector;

    if (!el) {
      throw new ReferenceError(`$selector is not an element.`);
    }

    const rect = el.getBoundingClientRect();
    const isVisible = rect.top < window.innerHeight && rect.bottom >= 0;

    return isVisible;
  }

  /**
   * Runs a function when the page stops scrolling.
   * @param {Function} func The function to run when scrolling is done.
   * @param {string} selector A query selector for the scrollable element.
   * @param {number} delay How long to wait after scrolling is done (ms).
   */
  static whenScrollDone(func, selector = 'window', delay = 100) {
    const el =
      selector === 'window' ? window : document.querySelector(selector);

    let timeout;

    const checkScroll = () => {
      clearTimeout(timeout);

      timeout = setTimeout(() => {
        func();
        el.removeEventListener('scroll', checkScroll);
      }, delay);
    };

    el.addEventListener('scroll', checkScroll);
  }

  /**
   * Adds a class to an element when it becomes visible in the viewport.
   *
   * @param {HTMLElement} el The element to watch.
   * @param {string} className The class name to add when visible.
   * @param {HTMLElement|null} appendTo The element to receive the class name.
   */
  static addClassWhenVisible(el, className = 'js-is-visible', appendTo = null) {
    const observer = new IntersectionObserver(
      ([e]) => {
        if (appendTo === null) {
          appendTo = e.target;
        }

        appendTo.classList.toggle(className, e.intersectionRatio >= 1);
      },
      { threshold: [1] },
    );

    observer.observe(el);
  }

  /**
   * Returns data about an image using canvas context's getImageData().
   *
   * @param {HTMLImageElement} image The image to use (must be loaded).
   * @param {number} canvasWidth The width of the canvas to use.
   * @return {Object} Image data.
   */
  static getImageData(image, canvasWidth = 500) {
    // Ensure image is loaded.
    if (image.naturalHeight === 0) {
      return undefined;
    }

    // Set up canvas
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    canvas.width = canvasWidth;
    canvas.height = (image.naturalHeight / image.naturalWidth) * canvas.width;

    // Draw image to canvas
    const sourceCoords = [0, 0, image.naturalWidth, image.naturalHeight];
    const destCoords = [0, 0, canvas.width, canvas.height];

    ctx.drawImage(image, ...sourceCoords, ...destCoords);

    try {
      return ctx.getImageData(...destCoords);
    } catch (err) {
      // Likely a CORS problem. Not much we can do about it.
      return null;
    }
  }

  /**
   * Appends a new style sheet to the HEAD element and returns it.
   *
   * @param {string} id An optional ID to add to the <style> element.
   * @param {string} className An optional class to add to the <style> element.
   *
   * @return {CSSStyleSheet} The newly created style sheet.
   */
  static createStyleSheet(id, className) {
    const style = document.createElement('style');

    if (id) {
      style.id = id;
    }

    if (className) {
      style.className = className;
    }

    // Fix issue with WebKit.
    style.appendChild(document.createTextNode(''));

    // Apply to the HEAD element.
    document.head.appendChild(style);

    return style.sheet;
  }
}
